import React, { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  useAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  useToggleAnnouncementPin,
  useCohorts,
  useLabsForAnnouncement,
} from '../hooks';
import { useToast } from '../components/Toast';
import type { Announcement, CreateAnnouncementInput } from '../types';
import Icon from '../components/Icon';
import styles from './Announcements.module.css';

const Announcements: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState<'all' | 'info' | 'warning' | 'success' | 'urgent'>('all');
  const [targetFilter, setTargetFilter] = useState<'all' | 'cohort' | 'lab'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateAnnouncementInput>({
    title: '',
    message: '',
    type: 'info',
    targetType: 'all',
    targetId: undefined,
    isPinned: false,
    sendEmail: false,
  });

  const { data, isLoading, refetch } = useAnnouncements({
    type: typeFilter === 'all' ? undefined : typeFilter,
    targetType: targetFilter === 'all' ? undefined : targetFilter,
  });
  const { data: cohorts } = useCohorts();
  const { data: labs } = useLabsForAnnouncement();

  const createAnnouncement = useCreateAnnouncement();
  const updateAnnouncement = useUpdateAnnouncement();
  const deleteAnnouncement = useDeleteAnnouncement();
  const togglePin = useToggleAnnouncementPin();
  const toast = useToast();

  const announcements = data?.data || [];

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      targetType: 'all',
      targetId: undefined,
      isPinned: false,
      sendEmail: false,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditingAnnouncement(null);
    setShowCreateModal(true);
  };

  const openEditModal = (announcement: Announcement) => {
    setFormData({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      targetType: announcement.targetType,
      targetId: announcement.targetId,
      isPinned: announcement.isPinned,
      sendEmail: false,
    });
    setEditingAnnouncement(announcement);
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingAnnouncement(null);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) return;

    try {
      if (editingAnnouncement) {
        await updateAnnouncement.mutateAsync({
          id: editingAnnouncement.id,
          data: formData,
        });
        toast.showSuccess('Announcement updated successfully');
      } else {
        await createAnnouncement.mutateAsync(formData);
        toast.showSuccess('Announcement created successfully');
      }
      closeModal();
      refetch();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      // Prefer a specific message (backend error body or a client-side guard,
      // e.g. "Select a cohort…") over the generic fallback.
      const axiosMsg = (error as { response?: { data?: { message?: string; error?: string } } })
        ?.response?.data;
      const specific =
        axiosMsg?.message ||
        axiosMsg?.error ||
        (error instanceof Error ? error.message : undefined);
      toast.showError(
        specific ||
          (editingAnnouncement ? 'Failed to update announcement' : 'Failed to create announcement'),
      );
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAnnouncement.mutateAsync(id);
      toast.showSuccess('Announcement deleted');
      setDeleteConfirm(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast.showError('Failed to delete announcement');
    }
  };

  const handleTogglePin = async (id: string, currentlyPinned: boolean) => {
    try {
      await togglePin.mutateAsync({ id, isPinned: !currentlyPinned });
      toast.showSuccess(currentlyPinned ? 'Announcement unpinned' : 'Announcement pinned');
      refetch();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.showError('Failed to update pin status');
    }
  };

  const getTypeIcon = (type: string): 'info' | 'warning' | 'check_circle' | 'error' => {
    switch (type) {
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'success':
        return 'check_circle';
      case 'urgent':
        return 'error';
      default:
        return 'info';
    }
  };

  const getTargetIcon = (targetType: string): 'groups' | 'school' | 'science' => {
    switch (targetType) {
      case 'all':
        return 'groups';
      case 'cohort':
        return 'school';
      case 'lab':
        return 'science';
      default:
        return 'groups';
    }
  };

  const getTargetLabel = (announcement: Announcement) => {
    switch (announcement.targetType) {
      case 'all':
        return 'All Students';
      case 'cohort':
        return announcement.targetName || 'Specific Cohort';
      case 'lab':
        return announcement.targetName || 'Specific Lab';
      default:
        return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <span>Loading announcements...</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Announcements</h1>
          <p>Create and manage announcements for your students</p>
        </div>
        <button className={styles.createBtn} onClick={openCreateModal}>
          <Icon name="add" size={20} />
          New Announcement
        </button>
      </header>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label>Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
            className={styles.select}
          >
            <option value="all">All Types</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="success">Success</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label>Audience:</label>
          <select
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value as typeof targetFilter)}
            className={styles.select}
          >
            <option value="all">All</option>
            <option value="cohort">Cohort</option>
            <option value="lab">Lab</option>
          </select>
        </div>
      </div>

      {/* Announcements List */}
      <div className={styles.announcementsList}>
        {announcements.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>megaphone</span>
            <p>No announcements yet</p>
            <span>Create your first announcement to reach your students</span>
          </div>
        ) : (
          announcements.map((announcement) => (
            <div
              key={announcement.id}
              className={`${styles.announcementCard} ${styles[announcement.type]}`}
            >
              <div className={styles.cardHeader}>
                <div className={styles.typeIcon}>
                  <Icon name={getTypeIcon(announcement.type)} size={20} />
                </div>
                <div className={styles.headerContent}>
                  <div className={styles.titleRow}>
                    <h3>{announcement.title}</h3>
                    {announcement.isPinned && (
                      <span className={styles.pinnedBadge}>
                        <Icon name="push_pin" size={14} />
                        Pinned
                      </span>
                    )}
                  </div>
                  <div className={styles.meta}>
                    <span className={styles.author}>By {announcement.authorName}</span>
                    <span className={styles.separator}>|</span>
                    <span className={styles.time}>
                      {formatDistanceToNow(new Date(announcement.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                <div className={styles.cardActions}>
                  <button
                    className={`${styles.iconBtn} ${announcement.isPinned ? styles.pinned : ''}`}
                    onClick={() => handleTogglePin(announcement.id, announcement.isPinned)}
                    title={announcement.isPinned ? 'Unpin' : 'Pin'}
                  >
                    <Icon name="push_pin" size={18} />
                  </button>
                  <button
                    className={styles.iconBtn}
                    onClick={() => openEditModal(announcement)}
                    title="Edit"
                  >
                    <Icon name="edit" size={18} />
                  </button>
                  <button
                    className={`${styles.iconBtn} ${styles.danger}`}
                    onClick={() => setDeleteConfirm(announcement.id)}
                    title="Delete"
                  >
                    <Icon name="delete" size={18} />
                  </button>
                </div>
              </div>

              <div className={styles.cardBody}>
                <p className={styles.message}>{announcement.message}</p>
              </div>

              <div className={styles.cardFooter}>
                <div className={styles.targetBadge}>
                  <Icon name={getTargetIcon(announcement.targetType)} size={16} />
                  {getTargetLabel(announcement)}
                </div>
                <div className={styles.readStats}>
                  <Icon name="visibility" size={16} />
                  {announcement.readCount} / {announcement.totalRecipients} read
                </div>
                {announcement.expiresAt && (
                  <div className={styles.expiry}>
                    <Icon name="schedule" size={16} />
                    Expires {format(new Date(announcement.expiresAt), 'MMM d, yyyy')}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}</h2>
              <button className={styles.closeBtn} onClick={closeModal}>
                <Icon name="close" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="title">Title *</label>
                <input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter announcement title..."
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Write your announcement message..."
                  rows={5}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="type">Type</label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as CreateAnnouncementInput['type'] })}
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="success">Success</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="targetType">Audience</label>
                  <select
                    id="targetType"
                    value={formData.targetType}
                    onChange={(e) => setFormData({
                      ...formData,
                      targetType: e.target.value as CreateAnnouncementInput['targetType'],
                      targetId: undefined,
                    })}
                  >
                    <option value="all">All Students</option>
                    <option value="cohort">Specific Cohort</option>
                    <option value="lab">Specific Lab</option>
                  </select>
                </div>
              </div>

              {formData.targetType === 'cohort' && cohorts && (
                <div className={styles.formGroup}>
                  <label htmlFor="targetId">Select Cohort</label>
                  <select
                    id="targetId"
                    value={formData.targetId || ''}
                    onChange={(e) => setFormData({ ...formData, targetId: e.target.value })}
                  >
                    <option value="">Select a cohort...</option>
                    {cohorts.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name} ({cohort.enrolledCount} students)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.targetType === 'lab' && labs && (
                <div className={styles.formGroup}>
                  <label htmlFor="targetId">Select Lab</label>
                  <select
                    id="targetId"
                    value={formData.targetId || ''}
                    onChange={(e) => setFormData({ ...formData, targetId: e.target.value })}
                  >
                    <option value="">Select a lab...</option>
                    {labs.map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={styles.formRow}>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={formData.isPinned}
                      onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                    />
                    <span className={styles.checkmark}></span>
                    Pin this announcement
                  </label>
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={formData.sendEmail}
                      onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
                    />
                    <span className={styles.checkmark}></span>
                    Also send email notification
                  </label>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button type="button" className={styles.cancelBtn} onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={createAnnouncement.isPending || updateAnnouncement.isPending}
                >
                  {(createAnnouncement.isPending || updateAnnouncement.isPending) ? (
                    'Saving...'
                  ) : editingAnnouncement ? (
                    'Update Announcement'
                  ) : (
                    'Create Announcement'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <Icon name="warning" size={48} />
            </div>
            <h3>Delete Announcement?</h3>
            <p>This action cannot be undone. The announcement will be permanently removed.</p>
            <div className={styles.confirmActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className={styles.deleteBtn}
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleteAnnouncement.isPending}
              >
                {deleteAnnouncement.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements;
